export const niceTickSteps = [1, 2, 2.5, 5, 10]

export function createNiceTicks(
  start: number,
  end: number,
  targetTickCount: number
) {
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return []
  }
  if (start === end) {
    return [start]
  }

  const direction = end >= start ? 1 : -1
  const min = Math.min(start, end)
  const max = Math.max(start, end)
  const span = max - min
  const roughStep = span / Math.max(targetTickCount - 1, 1)
  const exponent = Math.floor(Math.log10(Math.max(roughStep, Number.EPSILON)))
  const candidates = new Set<number>()

  for (let power = exponent - 1; power <= exponent + 1; power++) {
    const scale = 10 ** power
    niceTickSteps.forEach((step) => {
      candidates.add(step * scale)
    })
  }

  const scoredCandidates = Array.from(candidates)
    .filter((step) => step > 0)
    .map((step) => {
      const first = Math.ceil((min - Number.EPSILON) / step) * step
      const last = Math.floor((max + Number.EPSILON) / step) * step
      const ticks = first > last ? [] : buildTickRange(first, last, step)
      const count = ticks.length
      const distanceFromTarget = Math.abs(count - targetTickCount)
      const countPenalty =
        count < 3 ? (3 - count) * 10 : count > 7 ? (count - 7) * 10 : 0
      return {
        step,
        ticks,
        score: countPenalty + distanceFromTarget,
      }
    })
    .sort((left, right) => left.score - right.score || left.step - right.step)

  const bestTicks = scoredCandidates.find(
    (candidate) => candidate.ticks.length > 0
  )?.ticks ?? [min, max]

  return direction === 1 ? bestTicks : [...bestTicks].reverse()
}

export function formatTick(value: number) {
  if (!Number.isFinite(value)) return 'N/A'
  if (Math.abs(value) >= 100 || Number.isInteger(value)) {
    return value.toFixed(0)
  }
  if (Math.abs(value) >= 10) {
    return value.toFixed(1)
  }
  return value.toFixed(2)
}

export function buildTickRange(start: number, end: number, step: number) {
  const ticks: number[] = []
  const precision = Math.max(0, Math.ceil(-Math.log10(step)) + 2)
  for (let value = start; value <= end + step * 0.5; value += step) {
    ticks.push(Number(value.toFixed(precision)))
  }
  return ticks
}
